import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Trash2, AlertTriangle, Search, Loader2, CheckSquare, Square, Tag, MapPin,
  Skull, Boxes,
} from "lucide-react";
import { toast } from "sonner";

const getAuthConfig = () => {
  const token = localStorage.getItem("token");
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

/**
 * Inventory → Cleanup tab.
 *
 * Owner-only bulk-delete UI. Three explicit modes so the user picks the
 * scope they actually mean:
 *   • Single / multi-select items via checkboxes (search supported)
 *   • Whole category (drops the category record + all its items)
 *   • Whole location (multi-location store accounts only)
 *   • Nuke-everything button with a typed confirm-phrase guard
 *
 * Every destructive action goes through ConfirmDialog so a stray click
 * can't wipe data.
 */
const CleanupTab = ({ isStoreAccount, storeLocations }) => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Confirm dialog state
  const [confirm, setConfirm] = useState(null); // { mode, payload, label }
  const [confirmText, setConfirmText] = useState("");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes, catsRes] = await Promise.all([
        axios.get(`${API}/inventory/items`, getAuthConfig()),
        axios.get(`${API}/inventory/categories`, getAuthConfig()),
      ]);
      setItems(itemsRes.data || []);
      setCategories(catsRes.data || []);
    } catch (e) {
      toast.error("Failed to load inventory");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ─── Filtered items list ───
  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(it =>
      (it.name || "").toLowerCase().includes(q) ||
      (it.sku || "").toLowerCase().includes(q) ||
      (it.category || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  const toggleOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAllVisible = () => {
    setSelectedIds(prev => {
      const visible = filteredItems.map(i => i.item_id);
      const allSelected = visible.length > 0 && visible.every(id => prev.has(id));
      const next = new Set(prev);
      if (allSelected) visible.forEach(id => next.delete(id));
      else visible.forEach(id => next.add(id));
      return next;
    });
  };

  // ─── Mutations ───
  const runBulkDelete = async (payload) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/inventory/bulk-delete`, payload, getAuthConfig());
      const d = res.data || {};
      toast.success(
        `Deleted ${d.items_deleted || 0} item${d.items_deleted === 1 ? "" : "s"}` +
        (d.categories_deleted ? `, ${d.categories_deleted} categor${d.categories_deleted === 1 ? "y" : "ies"}` : "") +
        (d.tech_stock_rows_deleted ? `, ${d.tech_stock_rows_deleted} tech stock row${d.tech_stock_rows_deleted === 1 ? "" : "s"}` : "")
      );
      setSelectedIds(new Set());
      setConfirm(null);
      setConfirmText("");
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk delete failed");
    } finally {
      setBusy(false);
    }
  };

  const promptDeleteSelected = () => {
    if (selectedIds.size === 0) {
      toast.error("Select items first");
      return;
    }
    setConfirm({
      mode: "items",
      label: `Delete ${selectedIds.size} selected item${selectedIds.size === 1 ? "" : "s"}?`,
      payload: { mode: "items", item_ids: Array.from(selectedIds) },
    });
  };

  const promptDeleteCategory = (name) => {
    setConfirm({
      mode: "category",
      label: `Delete category "${name}" and all its items?`,
      payload: { mode: "category", category: name },
    });
  };

  const promptDeleteLocation = (loc) => {
    setConfirm({
      mode: "location",
      label: `Delete every item at "${loc.name}"?`,
      payload: { mode: "location", location_id: loc.location_id },
    });
  };

  const promptDeleteAll = () => {
    setConfirm({
      mode: "all",
      label: "Wipe ALL inventory (every item + every category)",
      payload: { mode: "all", confirm: "DELETE ALL" },
      requireType: "DELETE ALL",
    });
  };

  // ─── Counts per category for the "Categories" panel ───
  const itemsByCategory = useMemo(() => {
    const map = {};
    for (const it of items) {
      const k = it.category || "Uncategorized";
      map[k] = (map[k] || 0) + 1;
    }
    return map;
  }, [items]);

  const itemsByLocation = useMemo(() => {
    const map = {};
    for (const it of items) {
      const k = it.location_id || "_none";
      map[k] = (map[k] || 0) + 1;
    }
    return map;
  }, [items]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="inventory-cleanup-tab">
      {/* Top warning banner */}
      <div className="flex items-start gap-3 rounded-xl border border-orange-500/30 bg-orange-500/5 p-4" data-testid="cleanup-warning-banner">
        <AlertTriangle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-foreground font-['Manrope']">Bulk Delete Inventory</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Use these tools to remove items individually, by category, by location, or wipe the entire inventory.
            Every action requires confirmation. Tech stock counters for deleted items are also cleared automatically.
          </p>
        </div>
      </div>

      {/* ── Single / multi-select items ── */}
      <div className="rounded-xl border border-border/60 bg-card/40 backdrop-blur-sm p-4" data-testid="cleanup-items-panel">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Boxes className="w-4 h-4 text-blue-400" />
            <h3 className="text-sm font-semibold text-foreground">Select Items</h3>
            <Badge variant="outline" className="text-[10px]">{filteredItems.length} of {items.length}</Badge>
            {selectedIds.size > 0 && (
              <Badge className="text-[10px] bg-blue-500 text-white">{selectedIds.size} selected</Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search items, SKU, or category…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-8 text-xs w-56"
                data-testid="cleanup-search"
              />
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={toggleAllVisible}
              className="h-8 text-xs"
              data-testid="cleanup-toggle-all"
            >
              {filteredItems.every(i => selectedIds.has(i.item_id)) && filteredItems.length > 0
                ? <><CheckSquare className="w-3 h-3 mr-1" />Unselect Visible</>
                : <><Square className="w-3 h-3 mr-1" />Select Visible</>}
            </Button>
            <Button
              size="sm"
              onClick={promptDeleteSelected}
              disabled={selectedIds.size === 0 || busy}
              className="h-8 text-xs bg-red-500 hover:bg-red-600 text-white"
              data-testid="cleanup-delete-selected"
            >
              <Trash2 className="w-3 h-3 mr-1" />
              Delete {selectedIds.size > 0 ? `${selectedIds.size}` : "Selected"}
            </Button>
          </div>
        </div>

        <div className="max-h-[420px] overflow-y-auto rounded-lg border border-border/40">
          {filteredItems.length === 0 ? (
            <div className="text-center py-8 text-xs text-muted-foreground">
              {items.length === 0 ? "No inventory items." : "No items match your search."}
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-muted/30 sticky top-0 z-10">
                <tr className="text-left text-muted-foreground">
                  <th className="px-3 py-2 w-8" />
                  <th className="px-3 py-2 font-medium">Item</th>
                  <th className="px-3 py-2 font-medium">SKU</th>
                  <th className="px-3 py-2 font-medium">Category</th>
                  <th className="px-3 py-2 font-medium text-right">Stock</th>
                  <th className="px-3 py-2 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((it) => {
                  const checked = selectedIds.has(it.item_id);
                  return (
                    <tr key={it.item_id} className="border-t border-border/40 hover:bg-muted/20" data-testid={`cleanup-row-${it.item_id}`}>
                      <td className="px-3 py-2">
                        <Checkbox checked={checked} onCheckedChange={() => toggleOne(it.item_id)} data-testid={`cleanup-check-${it.item_id}`} />
                      </td>
                      <td className="px-3 py-2 font-medium text-foreground">{it.name}</td>
                      <td className="px-3 py-2 font-mono text-muted-foreground">{it.sku || "—"}</td>
                      <td className="px-3 py-2 text-muted-foreground">{it.category || "—"}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{it.stock_quantity ?? 0}</td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-red-400 hover:text-red-500 hover:bg-red-500/10"
                          onClick={() => setConfirm({
                            mode: "items",
                            label: `Delete "${it.name}"?`,
                            payload: { mode: "items", item_ids: [it.item_id] },
                          })}
                          data-testid={`cleanup-row-delete-${it.item_id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── By Category ── */}
      <div className="rounded-xl border border-border/60 bg-card/40 backdrop-blur-sm p-4" data-testid="cleanup-categories-panel">
        <div className="flex items-center gap-2 mb-3">
          <Tag className="w-4 h-4 text-violet-400" />
          <h3 className="text-sm font-semibold text-foreground">Delete by Category</h3>
          <Badge variant="outline" className="text-[10px]">{categories.length} categories</Badge>
        </div>
        {categories.length === 0 ? (
          <p className="text-xs text-muted-foreground">No categories yet.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {categories.map(name => (
              <div
                key={name}
                className="flex items-center justify-between rounded-lg border border-border/40 bg-background/40 px-3 py-2"
                data-testid={`cleanup-cat-${name}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-foreground truncate">{name}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {(itemsByCategory[name] || 0)} item{(itemsByCategory[name] || 0) === 1 ? "" : "s"}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 ml-2 text-red-400 hover:text-red-500 hover:bg-red-500/10"
                  onClick={() => promptDeleteCategory(name)}
                  data-testid={`cleanup-cat-delete-${name}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── By Location (multi-location only) ── */}
      {isStoreAccount && (storeLocations || []).length > 0 && (
        <div className="rounded-xl border border-border/60 bg-card/40 backdrop-blur-sm p-4" data-testid="cleanup-locations-panel">
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="w-4 h-4 text-emerald-400" />
            <h3 className="text-sm font-semibold text-foreground">Delete by Location</h3>
            <Badge variant="outline" className="text-[10px]">{storeLocations.length}</Badge>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {storeLocations.map(loc => (
              <div
                key={loc.location_id}
                className="flex items-center justify-between rounded-lg border border-border/40 bg-background/40 px-3 py-2"
                data-testid={`cleanup-loc-${loc.location_id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-foreground truncate">{loc.name}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {(itemsByLocation[loc.location_id] || 0)} item{(itemsByLocation[loc.location_id] || 0) === 1 ? "" : "s"}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 ml-2 text-red-400 hover:text-red-500 hover:bg-red-500/10"
                  onClick={() => promptDeleteLocation(loc)}
                  data-testid={`cleanup-loc-delete-${loc.location_id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Nuke everything ── */}
      <div className="rounded-xl border border-red-500/40 bg-red-500/5 p-4" data-testid="cleanup-danger-zone">
        <div className="flex items-start gap-3">
          <Skull className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-red-500 font-['Manrope']">Danger Zone — Wipe All Inventory</h3>
            <p className="text-xs text-muted-foreground mt-0.5 mb-3">
              Permanently removes <strong className="text-foreground">all {items.length} inventory items</strong>{" "}
              and <strong className="text-foreground">{categories.length} categories</strong>.
              Tech stock counters cleared too. This cannot be undone.
            </p>
            <Button
              size="sm"
              onClick={promptDeleteAll}
              className="h-8 text-xs bg-red-500 hover:bg-red-600 text-white"
              data-testid="cleanup-delete-all"
            >
              <Skull className="w-3 h-3 mr-1.5" />
              Delete All Inventory
            </Button>
          </div>
        </div>
      </div>

      {/* ── Confirm Dialog ── */}
      <Dialog
        open={!!confirm}
        onOpenChange={(o) => { if (!o) { setConfirm(null); setConfirmText(""); } }}
      >
        <DialogContent className="max-w-md" data-testid="cleanup-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-500">
              <AlertTriangle className="w-5 h-5" />
              Confirm Delete
            </DialogTitle>
            <DialogDescription className="text-foreground pt-2">
              {confirm?.label}
            </DialogDescription>
          </DialogHeader>
          {confirm?.requireType && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                Type <code className="px-1.5 py-0.5 rounded bg-muted text-foreground font-mono">{confirm.requireType}</code> to confirm.
              </p>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={confirm.requireType}
                autoFocus
                data-testid="cleanup-confirm-typed"
                className="font-mono"
              />
            </div>
          )}
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => { setConfirm(null); setConfirmText(""); }}
              disabled={busy}
              data-testid="cleanup-confirm-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={() => runBulkDelete(confirm.payload)}
              disabled={busy || (confirm?.requireType && confirmText !== confirm.requireType)}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="cleanup-confirm-confirm"
            >
              {busy
                ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Deleting…</>
                : <><Trash2 className="w-3.5 h-3.5 mr-1.5" />Confirm Delete</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CleanupTab;
